// One rendered PDF page: the page proxy loaded per page number, the canvas rendered at the device pixel ratio (capped by area) into a fresh canvas that replaces the old one only when complete, the selectable PDF.js text layer for copying and screen readers, and the overlay children positioned in the same CSS-pixel box.
import type { PageViewport, PDFDocumentProxy, PDFPageProxy } from "pdfjs-dist";
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";

import { cn } from "@/lib/cn";

import { loadPdfJs } from "./usePdfDocument";

const MAX_CANVAS_PIXELS = 16_777_216;

export interface PdfPageState {
  page: PDFPageProxy | null;
  error: unknown;
}

export function usePdfPage(doc: PDFDocumentProxy | null, pageNumber: number): PdfPageState {
  const [loaded, setLoaded] = useState<{
    doc: PDFDocumentProxy;
    number: number;
    page: PDFPageProxy | null;
    error: unknown;
  } | null>(null);
  useEffect(() => {
    if (doc === null) {
      return undefined;
    }
    let cancelled = false;
    doc.getPage(pageNumber).then(
      (page) => {
        if (!cancelled) {
          setLoaded({ doc, number: pageNumber, page, error: null });
        }
      },
      (error: unknown) => {
        if (!cancelled) {
          setLoaded({ doc, number: pageNumber, page: null, error });
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [doc, pageNumber]);
  return loaded?.doc === doc && loaded.number === pageNumber
    ? { page: loaded.page, error: loaded.error }
    : { page: null, error: null };
}

export function outputRatio(
  viewport: Pick<PageViewport, "width" | "height">,
  devicePixelRatio: number,
): number {
  const ratio = Math.max(1, devicePixelRatio || 1);
  const area = viewport.width * viewport.height * ratio * ratio;
  return area > MAX_CANVAS_PIXELS ? Math.sqrt(MAX_CANVAS_PIXELS / (viewport.width * viewport.height)) : ratio;
}

function isCancelled(error: unknown): boolean {
  return error instanceof Error && error.name === "RenderingCancelledException";
}

interface PdfPageProps {
  page: PDFPageProxy;
  viewport: PageViewport;
  label: string;
  onRendered?: (page: number) => void;
  onError?: (error: unknown) => void;
  className?: string;
  children?: ReactNode;
}

export function PdfPage({ page, viewport, label, onRendered, onError, className, children }: PdfPageProps) {
  const canvasHost = useRef<HTMLDivElement>(null);
  const textHost = useRef<HTMLDivElement>(null);
  const [rendered, setRendered] = useState<PageViewport | null>(null);
  const callbacks = useRef({ onRendered, onError });
  useEffect(() => {
    callbacks.current = { onRendered, onError };
  });

  useEffect(() => {
    const host = canvasHost.current;
    if (host === null) {
      return undefined;
    }
    const ratio = outputRatio(viewport, window.devicePixelRatio);
    const canvas = document.createElement("canvas");
    canvas.width = Math.floor(viewport.width * ratio);
    canvas.height = Math.floor(viewport.height * ratio);
    canvas.setAttribute("aria-hidden", "true");
    const task = page.render({
      canvas,
      viewport,
      ...(ratio === 1 ? {} : { transform: [ratio, 0, 0, ratio, 0, 0] }),
    });
    let cancelled = false;
    task.promise.then(
      () => {
        if (!cancelled) {
          host.replaceChildren(canvas);
          setRendered(viewport);
          callbacks.current.onRendered?.(page.pageNumber);
        }
      },
      (error: unknown) => {
        if (!cancelled && !isCancelled(error)) {
          callbacks.current.onError?.(error);
        }
      },
    );
    return () => {
      cancelled = true;
      task.cancel();
    };
  }, [page, viewport]);

  useEffect(() => {
    const host = textHost.current;
    if (host === null) {
      return undefined;
    }
    let cancelled = false;
    let layer: { cancel: () => void } | null = null;
    loadPdfJs()
      .then((pdfjs) => {
        if (cancelled) {
          return undefined;
        }
        host.replaceChildren();
        const textLayer = new pdfjs.TextLayer({
          textContentSource: page.streamTextContent({
            includeMarkedContent: true,
            disableNormalization: true,
          }),
          container: host,
          viewport,
        });
        layer = textLayer;
        return textLayer.render();
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      layer?.cancel();
    };
  }, [page, viewport]);

  const style = {
    width: viewport.width,
    height: viewport.height,
    "--total-scale-factor": viewport.scale,
  } as CSSProperties;
  return (
    <div
      className={cn("pdf-page", className)}
      style={style}
      role="group"
      aria-label={label}
      data-page={page.pageNumber}
      data-rendered={rendered === viewport ? "true" : "false"}
    >
      <div ref={canvasHost} className="pdf-page__canvas" />
      <div ref={textHost} className="pdf-text-layer" />
      {children}
    </div>
  );
}

export function PagePlaceholder({
  width,
  height,
  className,
}: {
  width: number;
  height: number;
  className?: string;
}) {
  return (
    <div
      className={cn("pdf-page flex flex-col gap-3 overflow-hidden p-[8%] opacity-80", className)}
      style={{ width, height }}
      aria-hidden
    >
      {Array.from({ length: 16 }, (_, index) => (
        <div
          key={index}
          className={cn("h-2.5 animate-pulse rounded-sm bg-[#eceff3]", index % 4 === 0 ? "w-2/3" : "w-full")}
        />
      ))}
    </div>
  );
}
